import React, { useRef, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useConfirm } from 'hooks';

import i18n from 'i18n-js';
import { buildUrl } from 'utils';
import Layout from 'components/Layout';
import Box from '@material-ui/core/Box';
import Wizard from 'components/UI/Wizard';
import Grid from '@material-ui/core/Grid';
import { uiActions } from 'state/modules/UI';
import { useStyles } from './InvoiceWizard.styled';
import Container from '@material-ui/core/Container';
import Typography from '@material-ui/core/Typography';
import { InvoiceActions } from 'state/modules/invoice';
import { BasicInfo, Details } from 'components/Invoice';
import { useHistory, useParams } from 'react-router-dom';

export const InvoiceWizard = ({ isStatementEntry }) => {
  const classes = useStyles();
  const formRef = useRef(null);
  const history = useHistory();
  const dispatch = useDispatch();
  const { id, action } = useParams();

  const loading = useSelector((state) => state.invoice.loading);
  const invoice = useSelector((state) => state.invoice.invoice);
  const { exitStep, position, exitOnboarding, errors, steps, lastStep } = useSelector(
    (state) => state.invoice.wizard
  );

  const title = isStatementEntry
    ? i18n.t('statement_entries.create_new_invoice')
    : i18n.t('invoice.create_new_invoice');
  const wizardTitle = isStatementEntry
    ? i18n.t('statement_entries.wizard_title')
    : i18n.t('invoice.wizard.title');
  const successMessage = isStatementEntry
    ? i18n.t('statement_entries.invoice_created_successfully')
    : i18n.t('invoice.invoice_created_successfully');
  const detailsLabel = isStatementEntry
    ? i18n.t('statement_entries.wizard_details_label')
    : i18n.t('invoice.wizard.details');

  const stepsList = [
    {
      position: 1,
      key: 'basic_info',
      label: i18n.t('invoice.wizard.basic_info'),
    },
    {
      position: 2,
      key: 'details',
      label: detailsLabel,
    },
  ];

  const onPrevious = () => {
    dispatch(InvoiceActions.changePosition('prev'));
  };

  const handleNextPosition = () => {
    dispatch(InvoiceActions.changePosition('next'));
  };

  const handleEndOnboarding = (ev, type, message) => {
    if (formRef?.current && type !== 'cancel') {
      return formRef.current.submitForm();
    }
    onExitOnboarding(type, message);
  };

  const handleDiscard = () => {
    dispatch(InvoiceActions.fetchDeleteInvoice(invoice?.id)).then(() =>
      history.push(buildUrl(isStatementEntry ? '/statement-entries' : '/invoices'))
    );
    dispatch(InvoiceActions.resetWizard());
  };

  const onExitOnboarding = (type, message) => {
    dispatch(InvoiceActions.resetWizard());
    if (type !== 'cancel') {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: message || successMessage,
        })
      );
    }
    history.push(buildUrl(isStatementEntry ? '/statement-entries' : '/invoices'));
  };

  const handleNextStep = () => {
    switch (position) {
      case 0:
        if (formRef?.current) {
          return formRef.current.submitForm();
        }
        break;
      case 1:
        dispatch(InvoiceActions.fetchPublishInvoice(true));
        break;
      default:
        break;
    }
  };

  const handleDraft = () => {
    handleEndOnboarding(null, 'draft', i18n.t('invoice.created_draft'));
  };

  const onSaveAndExit = () => {
    handleNextStep(true);
  };

  const handleExit = (exit) => {
    dispatch(InvoiceActions.setExit(exit || !exitStep, 'exitStep'));
  };

  useEffect(() => {
    if (
      errors &&
      lastStep &&
      (Array.isArray(errors) ||
        Object.keys(errors).length !== 0 ||
        Array.isArray(steps?.details?.vehicle_association_requirements))
    ) {
      handleEndOnboarding();
    } else if (lastStep) {
      displaySuccessModal();
    }
  }, [lastStep]); // eslint-disable-line

  /**
   * Get invoice by id
   */
  const fetchInvoiceById = useCallback(
    (id) => {
      dispatch(InvoiceActions.fetchInvoiceById(id));
    },
    [dispatch]
  );

  useEffect(() => {
    if (id) {
      fetchInvoiceById(id);
    }
    return () => {
      dispatch(InvoiceActions.resetWizard());
    };
  }, [id, fetchInvoiceById, dispatch]);

  useEffect(() => {
    if (exitOnboarding) {
      onExitOnboarding();
    }
  }, [exitOnboarding]); // eslint-disable-line

  const displaySuccessModal = () => {
    handleEndOnboarding();
  };

  const [toggleDiscard, DiscardConfirm] = useConfirm({
    title: isStatementEntry
      ? i18n.t('statement_entries.discard_title')
      : i18n.t('invoice.discard_title'),
    message: isStatementEntry
      ? i18n.t('statement_entries.discard_msg')
      : i18n.t('invoice.discard_msg'),
    cancelText: i18n.t('common.cancel'),
    confirmText: i18n.t('common.discard'),
    onConfirm: handleDiscard,
  });
  const [toggleSave, SaveConfirm] = useConfirm({
    title: isStatementEntry ? i18n.t('statement_entries.save_title') : i18n.t('invoice.save_title'),
    message: isStatementEntry ? i18n.t('statement_entries.save_msg') : i18n.t('invoice.save_msg'),
    cancelText: i18n.t('common.cancel'),
    confirmText: i18n.t('common.ok'),
    onConfirm: handleDraft,
  });
  const [toggleSubmit, SubmitConfirm] = useConfirm({
    title: isStatementEntry
      ? i18n.t('statement_entries.submit_title')
      : i18n.t('invoice.submit_title'),
    message: isStatementEntry
      ? i18n.t('statement_entries.submit_msg')
      : i18n.t('invoice.submit_msg'),
    cancelText: i18n.t('common.cancel'),
    confirmText: i18n.t('common.submit'),
    onConfirm: onSaveAndExit,
  });

  return (
    <Layout title={title}>
      <DiscardConfirm />
      <SaveConfirm />
      <SubmitConfirm />
      <Container maxwidth="lg">
        <Grid container direction="row" justifyContent="center" alignItems="center">
          <Grid item xs={12}>
            <Grid
              container
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              style={{ marginBottom: 48 }}
            >
              <Grid item>
                <Typography variant="h6" gutterBottom>
                  <Box fontWeight="fontWeightBold" fontSize={24} mb={1}>
                    {title}
                  </Box>
                </Typography>
              </Grid>
              {invoice?.id && (
                <Grid item>
                  <Typography variant="h6" gutterBottom>
                    <Box fontWeight="fontWeightBold" fontSize={24} mb={1} data-cy="invoice-id">
                      {i18n.t('common.id')} #{invoice?.id}
                    </Box>
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Wizard
              mode="create"
              exit={exitStep}
              steps={stepsList}
              loading={loading}
              position={position}
              handleExit={handleExit}
              onNext={position === 0 ? handleNextStep : toggleSubmit}
              onPrevious={onPrevious}
              onSaveDraft={toggleSave}
              onSaveAndExit={onSaveAndExit}
              successMessage={successMessage}
              displayDraftButton={position === 1}
              lastStepLabel={i18n.t('common.submit')}
              title={wizardTitle}
              handleEndOnboarding={handleEndOnboarding}
              customStepperClass={classes.stepperContainer}
              displayDiscardButton={!!invoice?.id}
              onDiscard={toggleDiscard}
              saveButtonText={position === 0 ? i18n.t('common.save_and_next') : undefined}
              showCancelButton={!invoice?.id}
            >
              {position === 0 && (
                <BasicInfo
                  action={action}
                  exit={exitStep}
                  errors={errors}
                  formRef={formRef}
                  handleNextPosition={handleNextPosition}
                />
              )}
              {position === 1 && (
                <Details
                  currentVehicle={steps.details}
                  step={steps.connection}
                  isStatementEntry={isStatementEntry}
                />
              )}
            </Wizard>
          </Grid>
        </Grid>
      </Container>
    </Layout>
  );
};
